import { useCallback, useEffect, useState } from 'react'

import useRedux from '../../../hooks/useRedux'
import { setErrorCodeData } from '../../../redux/jobs/action'
import debounce from '../../../utils/debounce'
import { isEqual, length, notEqual, ternary } from '../../../utils/javascript'
import { hasOnlySpace } from '../../../utils/validation'
import {
  addErrorCodeApi,
  editErrorCodeApi,
  getErrorCodeApi,
  searchErrorCodeApi,
} from '../jobs.api'

const errorCodeField = () => {
  const { dispatch, selector } = useRedux()
  const catalogueSelected = selector(state => state?.jobs?.catalogueSelected)
  const { customId, modelInfo, productSeriesInfo } =
    catalogueSelected?.[0] || {}
  const productSeriesId = productSeriesInfo?.id
  const modelId = modelInfo?.id
  const errorCode = selector(state => state?.jobs?.errorCode)
  const { value, options, selectedOption } = {
    ...errorCode,
  }
  const [popConfirm, setPopConfirm] = useState({
    open: false,
    tempSelected: null,
  })
  const [search, setSearch] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [loading, setLoading] = useState(false)
  const pageSize = 10
  const isCustom = modelId && isEqual(modelId, customId)
  const addNewDisabled = !isCustom || length(options?.list) || options?.loader

  useEffect(() => {
    modelId && !search && getSelectOptions({})
  }, [modelId, search])

  const getSelectOptions = async ({ pageNo = 1, options, searchValue }) => {
    if (!productSeriesId) return
    dispatch(setErrorCodeData({ options: { ...options, loader: true } }))
    let params = `?pageSize=${pageSize}&productSeriesId=${productSeriesId}`

    let resp = {}
    if (search || searchValue) {
      params += `&searchValue=${searchValue}`
      resp = await searchErrorCodeApi({
        pageNo,
        params,
      })
    } else {
      resp = await getErrorCodeApi({
        pageNo,
        params,
      })
    }

    dispatch(
      setErrorCodeData({
        options: {
          ...resp?.data,
          loader: false,
          list: [
            ...ternary(isEqual(pageNo, 1), [], options?.list),
            ...(resp?.data?.list || []),
          ],
        },
      }),
    )
  }

  const handleOnChange = ({ value, option, showConfirm }) => {
    if (showConfirm)
      return setPopConfirm({ open: true, tempSelected: { value, option } })
    const payload = {
      value,
      selectedOption: option,
    }
    dispatch(setErrorCodeData(payload))
    setSearch('')
  }

  const debounceGetOptions = useCallback(debounce(getSelectOptions, 500), [
    productSeriesId,
  ])

  const onInputChange = e => {
    const { value } = e?.target
    setSearch(value)
    !hasOnlySpace(value) && debounceGetOptions({ searchValue: value, options })
  }

  const handleAddNewItem = async () => {
    setLoading(true)
    const payload = {
      productSeriesModelId: productSeriesId,
      name: search,
      description: '',
    }
    let resp
    if (isEdit) {
      resp = await editErrorCodeApi({
        payload: {
          ...payload,
          customErrorCodeId: value,
          description: options?.list?.find(({ id }) => isEqual(id, value))
            ?.description,
        },
      })
      setIsEdit(false)
    } else {
      resp = await addErrorCodeApi({ payload })
    }
    setLoading(false)
    const { id, name } = { ...resp?.data }
    if (!id) return //api failure
    dispatch(
      setErrorCodeData({
        value: id,
        selectedOption: { value: id, label: name },
        options: { list: [resp?.data] },
      }),
    )
    setSearch('')
    setIsOpen(false)
  }

  const onScroll = debounce(e => {
    const { target } = e
    const { pageNo, hasMore } = { ...options }
    if (
      hasMore &&
      isEqual(target.scrollHeight - target.scrollTop, target.clientHeight)
    ) {
      getSelectOptions({ pageNo: pageNo + 1, options, searchValue: search })
    }
  }, 300)

  const addSelectedOption = list => {
    const newList = []
    list?.forEach(({ name, id }) => {
      if (notEqual(selectedOption?.value, id))
        newList.push({
          label: name,
          value: id,
        })
    })
    return ternary(
      !search && selectedOption?.value,
      [selectedOption, ...newList],
      newList,
    )
  }

  const onDropdownVisible = visible => {
    setIsOpen(visible)
    // setSearch('')
    setIsEdit(false)
  }
  const onClosePopup = () => setPopConfirm({ open: false, tempSelected: null })

  const handleEditClick = (e, option) => {
    e.stopPropagation()
    setSearch(option?.label)
    setIsEdit(true)
  }

  return {
    value,
    search,
    loading,
    isOpen,
    isEdit,
    options,
    isCustom,
    popConfirm,
    addNewDisabled,
    onScroll,
    onClosePopup,
    onInputChange,
    handleOnChange,
    handleEditClick,
    handleAddNewItem,
    addSelectedOption,
    onDropdownVisible,
  }
}

export default errorCodeField
